import TileState from "ol/TileState";

// Retry a transient tile failure once, with a short backoff. Kept deliberately
// small: the retry is awaited inside tileLoadFunction, so the tile holds one of
// OL's concurrent tile-queue slots (maxTilesLoading, default 16) until it
// settles. 429 is excluded — retrying a rate-limiter on a 1s backoff just earns
// another 429.
const RETRY_STATUSES = [408, 500, 502, 503, 504];
const MAX_RETRIES = 1;
const RETRY_BACKOFF_MS = 1000;

/**
 * Builds a `fetch`-based OpenLayers `tileLoadFunction` for one XYZ tile source,
 * plus a `handleVisibility` hook to wire to the layer's `change:visible`:
 * - injects an optional `Authorization: Bearer <token>` header (via `getToken`),
 * - retries a transient HTTP failure once,
 * - cancels every in-flight request when the layer is hidden (so a basemap you
 *   switched away from stops loading) and re-fetches on return via
 *   `source.refresh()` — but only if requests were actually aborted, so a
 *   fully-loaded layer keeps its cached tiles.
 */
export function createManagedTileLoader({ getToken = () => null } = {}) {
  let controller = new AbortController();
  let inflight = 0;
  let wasAborted = false;

  function tileLoadFunction(tile, src) {
    const signal = controller.signal;
    inflight++;
    (async () => {
      try {
        for (let attempt = 0; ; attempt++) {
          if (signal.aborted) throw new Error("aborted");
          const response = await fetch(src, buildInit(getToken(), signal));
          if (
            RETRY_STATUSES.includes(response.status) &&
            attempt < MAX_RETRIES &&
            !signal.aborted
          ) {
            await sleep(RETRY_BACKOFF_MS);
            continue;
          }
          if (!response.ok) throw new Error("HTTP " + response.status);
          const blob = await response.blob();
          if (signal.aborted) throw new Error("aborted");
          const url = URL.createObjectURL(blob);
          const image = tile.getImage();
          image.src = url;
          const revoke = () => URL.revokeObjectURL(url);
          image.addEventListener("load", revoke, { once: true });
          image.addEventListener("error", revoke, { once: true });
          return;
        }
      } catch {
        tile.setState(TileState.ERROR);
      } finally {
        inflight--;
      }
    })();
  }

  function handleVisibility(visible, source) {
    if (!visible) {
      // Only abort (and flag a reload) if requests were actually in flight — a
      // layer that finished loading before being hidden keeps its cache.
      if (inflight > 0) {
        controller.abort();
        controller = new AbortController();
        wasAborted = true;
      }
    } else if (wasAborted) {
      wasAborted = false;
      if (source) source.refresh();
    }
  }

  return { tileLoadFunction, handleVisibility };
}

function buildInit(token, signal) {
  const init = { signal };
  if (token) init.headers = { Authorization: "Bearer " + token };
  return init;
}

function sleep(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}
